import { apiClient } from '@/lib/apiClient';

/**
 * The online import's API (integration_architecture.md, Online import): import a CSV or XLSX from
 * an entity's list view, mapped on the fly, without a saved profile or an integration job.
 */

/** The permission that opens the online import; the entity's own Create/Update still apply. */
export const ONLINE_IMPORT_PERMISSION = 'Integration.IntegrationRun.OnlineImport';

const BASE = '/api/integration/onlineimport';

/** How long the client waits for a file to be read. */
export const ANALYZE_TIMEOUT_MS = 120_000;

export interface ImportFieldDescriptor {
  name: string;
  label: string;
  type: string;
  onCreate?: boolean;
  onUpdate?: boolean;
  requiredOnCreate?: boolean;
  isKey?: boolean;
  serverAssigned?: boolean;
  referencedEntity?: string | null;
  referencedModule?: string | null;
  hierarchyKey?: string | null;
}

export interface OnlineImportTarget {
  entityName: string;
  moduleName: string;
  keyFields: string[];
  fields: ImportFieldDescriptor[];
  canCreate: boolean;
  canUpdate: boolean;
  maxRows: number;
  maxRequestBytes: number;
  requestTimeoutSeconds: number;
}

export interface OnlineImportFormat {
  delimiter?: string | null;
  hasHeader: boolean;
  encoding?: string | null;
  worksheetName?: string | null;
  headerRowIndex?: number | null;
  dataStartRowIndex?: number | null;
  /**
   * How the file writes numbers, dates and times (ISO, sv-SE, en-US) — an Import Profile's.
   * Absent: the person's own "Date, time and number format", which a list view's import uses.
   */
  valueFormat?: string | null;
}

export interface OnlineImportMapping {
  sourceColumn: string;
  targetField?: string | null;
  transformChain?: string | null;
  defaultValue?: string | null;
}

export interface OnlineImportWarning {
  code: 'AmbiguousDecimal' | 'AmbiguousDate' | string;
  column: string;
  example?: string | null;
  formats: string[];
}

export interface OnlineImportAnalysis {
  format: 'CSV' | 'XLSX' | string;
  delimiter?: string | null;
  worksheets: string[];
  worksheet?: string | null;
  headers: string[];
  rowCount: number;
  exceedsMaxRows: boolean;
  sampleRows: Record<string, string>[];
  suggestedMappings: OnlineImportMapping[];
  warnings: OnlineImportWarning[];
}

/** Why a row cannot be imported, or something worth knowing; `code` and `args` word it, `message` is the English fallback. */
export interface ImportIssue {
  severity: 'Error' | 'Info';
  field?: string | null;
  code: string;
  message: string;
  args: Record<string, string>;
}

export type ImportRowOutcome = 'Create' | 'Update' | 'Error';

export interface OnlineImportPreviewRow {
  /** The row's number in the file, header included. */
  rowNumber: number;
  outcome: ImportRowOutcome;
  existingId?: string | null;
  key?: string | null;
  issues: ImportIssue[];
  /**
   * What the import reads for each mapped field, by the mapping's field name: the cell after the
   * column's transforms, a reference as its key, or a default for an empty cell of a new record.
   * A field the import leaves as it is is absent.
   */
  values: Record<string, string>;
  /** The file's cell, for each field whose transforms changed it. */
  fileValues: Record<string, string>;
  /** Fields whose value is a default: the cell is empty and the row creates a record. */
  defaulted: string[];
  /** Fields whose empty cell empties them on update (the `clear` transform). */
  cleared: string[];
}

/** What importing the file would do, row by row, from a dry run that wrote nothing. */
export interface OnlineImportPreview {
  total: number;
  creates: number;
  updates: number;
  errors: number;
  rows: OnlineImportPreviewRow[];
}

export interface OnlineImportResult {
  runId: string;
  status: string;
  total: number;
  created: number;
  updated: number;
  failed: number;
  failedRows: { rowNumber: number; message: string }[];
}

/**
 * A file sent to be read as an import reads it — the online import's and an Import Profile's "Load
 * from File": the file and the settings as JSON. The Content-Type is said outright because the
 * client's default is application/json, and axios turns FormData into JSON under it; with
 * multipart/form-data named, the browser sends the form and adds the boundary itself.
 */
export function postFileUpload<T>(url: string, file: File, request: unknown, timeoutMs: number): Promise<T> {
  const form = new FormData();
  form.append('file', file);
  form.append('request', JSON.stringify(request));
  return apiClient
    .post<T>(url, form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: timeoutMs })
    .then((response) => response.data);
}

function upload<T>(path: string, file: File, request: unknown, timeoutMs: number): Promise<T> {
  return postFileUpload<T>(`${BASE}/${path}`, file, request, timeoutMs);
}

export const onlineImportService = {
  getTarget: (entityName: string): Promise<OnlineImportTarget> =>
    apiClient.get<OnlineImportTarget>(`${BASE}/targets/${encodeURIComponent(entityName)}`).then((r) => r.data),

  analyze: (file: File, targetEntity: string, format: OnlineImportFormat): Promise<OnlineImportAnalysis> =>
    upload('analyze', file, { targetEntity, format }, ANALYZE_TIMEOUT_MS),

  /** A dry run: what each row would do. As slow as reading the records an import would touch. */
  preview: (
    file: File,
    targetEntity: string,
    format: OnlineImportFormat,
    mappings: OnlineImportMapping[],
    requestTimeoutSeconds: number,
  ): Promise<OnlineImportPreview> =>
    upload('preview', file, { targetEntity, format, mappings }, (requestTimeoutSeconds + 15) * 1000),

  /** Waits as long as the server lets an import run (`requestTimeoutSeconds`) and a little more. */
  importFile: (
    file: File,
    targetEntity: string,
    format: OnlineImportFormat,
    mappings: OnlineImportMapping[],
    requestTimeoutSeconds: number,
  ): Promise<OnlineImportResult> =>
    upload('import', file, { targetEntity, format, mappings }, (requestTimeoutSeconds + 15) * 1000),
};
